/**
 * Formats a date object to a string in the ISO date format (YYYY-MM-DD) in the Date's local time zone.
 * @param date The date object to format.
 * @returns The formatted date string or an empty string if the input date is missing or invalid.
 * @example
 * dateToString(new Date('2021-01-01T00:00:00')) // '2021-01-01'
 * dateToString(null) // ''
 */
export const dateToString = (date?: Date | null) =>
  !date || isNaN(date.getTime())
    ? ''
    : [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
      ].join('-');

export const isPast = (date: Date) => date < new Date();

export const differenceInSeconds = (date1: Date, date2: Date) =>
  (date1.valueOf() - date2.valueOf()) / 1000;

export const differenceInMinutes = (date1: Date, date2: Date) =>
  differenceInSeconds(date1, date2) / 60;

export const differenceInHours = (date1: Date, date2: Date) =>
  differenceInMinutes(date1, date2) / 60;

export const differenceInDays = (date1: Date, date2: Date) => differenceInHours(date1, date2) / 24;

export const differenceInMonths = (date1: Date, date2: Date) => {
  const months = (date1.getFullYear() - date2.getFullYear()) * 12;
  return months + date1.getMonth() - date2.getMonth();
};

const isSameDay = (date1: Date, date2: Date) =>
  date1.getDate() === date2.getDate() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getFullYear() === date2.getFullYear();

export const isToday = (date: Date) => {
  const now = new Date();
  return isSameDay(date, now);
};

export const isTomorrow = (date: Date) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return isSameDay(date, tomorrow);
};

export const isBefore = (date1: Date, date2: Date) => date1 < date2;

export const isAfter = (date1: Date, date2: Date) => date1 > date2;

type Unit = 'seconds' | 'minutes' | 'hours' | 'days' | 'years';
export const dateAdd = (date: Date, duration: number, unit: Unit) => {
  const resultDate = new Date(date);
  switch (unit) {
    case 'seconds':
      resultDate.setSeconds(resultDate.getSeconds() + duration);
      break;
    case 'minutes':
      resultDate.setMinutes(resultDate.getMinutes() + duration);
      break;
    case 'hours':
      resultDate.setHours(resultDate.getHours() + duration);
      break;
    case 'days':
      resultDate.setDate(resultDate.getDate() + duration);
      break;
    case 'years':
      resultDate.setFullYear(resultDate.getFullYear() + duration);
      break;
  }
  return resultDate;
};

export const endOfDay = (date: Date) => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  end.setMinutes(end.getMinutes() - end.getTimezoneOffset());
  return end;
};

export const startOfDay = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setMinutes(start.getMinutes() - start.getTimezoneOffset());
  return start;
};

type ParseDate = {
  (date: string): Date;
  (date: string | null | undefined, nullable: true): Date | null;
};

/**
 * Parses a date string to a Date object in the local time zone.
 * It should only be used with date string as the behavior with DateTime strings is not guaranteed.
 * @param date The date string to parse; could be null or undefined if `nullable` is true.
 * @param nullable If true, the function returns null if the input date is missing.
 * @returns The parsed Date object or null if the input date is missing and `nullable` is true.
 * @example
 * parseDate('2021-01-01') // Date object representing January 1, 2021
 * parseDate('2021-01-01', true) // Date object representing January 1, 2021
 * parseDate(null, true) // null
 */
export const parseDate: ParseDate = (date: string | null | undefined, nullable = false) => {
  if (nullable && (!date || !date.trim())) {
    return null as unknown as Date;
  }

  if (date!.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(`${date}T00:00:00`);
  }

  return new Date(date!);
};

export type Period =
  | 'last_week'
  | 'last_2_weeks'
  | 'last_30_days'
  | 'previous_month'
  | 'last_6_month';
// FIXME: This is returning inconsistent date ranges, needs more scrutiny.
export const periodToDate = (period: Period) => {
  const startDate = new Date();
  const endDate = new Date();
  // Set the end date to the next day to include the whole day
  endDate.setDate(endDate.getDate() + 1);

  switch (period) {
    case 'last_week':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'last_2_weeks':
      startDate.setDate(startDate.getDate() - 14);
      break;
    case 'last_30_days':
      startDate.setDate(startDate.getDate() - 30);
      break;
    case 'previous_month':
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(0);
      endDate.setMonth(endDate.getMonth() - 1);
      endDate.setDate(32);
      break;
    case 'last_6_month':
      startDate.setMonth(startDate.getMonth() - 6);
      break;
  }

  return { startDate: dateToString(startDate), endDate: dateToString(endDate) };
};

/**
 * Extracts the time part of a date object or an ISO date string, resulting in UTC time.
 * @param date The date object or ISO date string to extract the time from.
 * @returns The time part of the input date in the format 'HH:mm'.
 * @example
 * extractTime(new Date('2021-01-01T12:34:56')) // '11:34' in UTC+1
 * extractTime('2021-01-01T12:34:56Z') // '12:34'
 */
export const extractTime = (date: Date | string) =>
  (typeof date === 'string' ? date : date.toISOString()).slice(11, 16);
